/**
 * @file 任务重Evo阶段 重新续跑
 * @author sunyingying01
 */

import {makeObservable, observable, action, computed, flow, flowResult} from 'mobx';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type {JobStore} from './jobs/JobStore';

/**
 * 已选择的解数据结构
 */
export interface ISelectedSolution {
    /** 节点ID */
    id: string;
    /** 岛屿编号 */
    island: string;
    /** 代际 */
    generation: number;
    /** 得分 */
    score: number;
    /** 原始数据(包含完整信息) */
    raw?: any;
}

export class TaskEvoRunStore {
    static limitSelectSolutionsNumber = 15;
    /** 任务id */
    job_id: string = '';

    /** 所有续跑解的列表（推荐解 + 普通解） */
    allRecommendedSolutions: InitialSolutionFileInfo[] = [];

    /** 系统推荐解表格数据（后端返回的特定推荐解） */
    systemRecommendedSolution: InitialSolutionFileInfo[] = [];

    /** 选中的优质算法集数据 */
    selectedHighQualityAlgorithms: any[] = [];

    // /** 已选择的解列表 (用于"选取优质解继续演化"功能) */
    selectedSolutions: InitialSolutionFileInfo[] = [];

    /** 持有 JobsListStore 的引用 (用于访问任务列表信息) */
    selectedJobDetail: JobStore | null = null;

    /** 防抖定时器 */
    private debounceTimer: NodeJS.Timeout | null = null;

    constructor(job_id: string = '', selectedJobDetail?: JobStore) {
        this.job_id = job_id;
        this.selectedJobDetail = selectedJobDetail || null;
        makeObservable(this, {
            job_id: observable,
            allRecommendedSolutions: observable,
            systemRecommendedSolution: observable,
            selectedHighQualityAlgorithms: observable,
            selectedSolutions: observable,
            selectedSolutionsCount: computed,
            // 新增: 自动筛选推荐解
            filteredSelectedSolutions: computed,
            disabledSelectSolutions: computed,
            selectedSolutionIds: computed,
            clearSolutions: action.bound,
            hasSolution: action.bound,
            setRecommendedSolutions: action.bound,
            setSystemRecommendedSolution: action.bound,
            isSolutionSelected: action.bound,
            syncSelectedSolutionsToBackend: flow.bound,
            updateSystemRecommendedSelection: action.bound,
            addSolution: action.bound,
            removeSolution: action.bound,
            setDefaultSelectedSolutions: action.bound,
            updateSelectedJobDetail: action.bound
        });
    }

    /**
     * 获取已选择解的数量
     */
    get selectedSolutionsCount(): number {
        return this.selectedSolutions.length;
    }

    /**
     * 根据 selectedJobDetail.select_solutions 从 allRecommendedSolutions 中筛选出选中的解
     * @returns 筛选后的续跑解列表
     */
    get filteredSelectedSolutions(): InitialSolutionFileInfo[] {
        if (
            !this.selectedJobDetail
            || !this.selectedJobDetail.select_solutions
        ) {
            return [];
        }

        const selectedIds = this.selectedJobDetail.select_solutions;

        return this.allRecommendedSolutions.filter(solution =>
            selectedIds.includes(solution.s_id)
        );
    }

    // 是否可以勾选优质解
    get disabledSelectSolutions(): boolean {
        return (
            this.selectedSolutions.length
                >= TaskEvoRunStore.limitSelectSolutionsNumber
        );
    }

    /**
     * 获取已选择解的 s_id 集合(用于快速查找)
     */
    get selectedSolutionIds(): Set<string> {
        return new Set(this.selectedSolutions.map(s => s.s_id));
    }

    /**
     * 判断指定 s_id 的解是否已被选中
     * @param s_id 解的 s_id
     * @returns 是否已选中
     */
    isSolutionSelected(s_id: string): boolean {
        return this.selectedSolutionIds.has(s_id);
    }

    setAllRecommendedSolutions(list: InitialSolutionFileInfo[]) {
        this.allRecommendedSolutions = list;
    }

    /**
     * 同步选中解列表到后端
     * @param solutions 最新的选中解列表
     */
    * syncSelectedSolutionsToBackend(
        this: TaskEvoRunStore,
        params: {
            solutions?: InitialSolutionFileInfo[];
            evolve_config?: Record<string, any>;
            reqdetails?: boolean;
        } = {
            solutions: this.selectedSolutions,
            evolve_config:
                this.selectedJobDetail?.descParameterStore?.reEvolveConfig,
            reqdetails: true
        }
    ) {
        if (!this.selectedJobDetail) {
            console.warn(
                '[syncSelectedSolutionsToBackend] selectedJobDetail 不存在'
            );
            return;
        }
        try {
            const select_solutions = params.solutions ? params.solutions : this.selectedSolutions;
            // 获取 evolve_config 并去除 enable_cold_start 和 model 参数
            const evolveConfig =
                params.evolve_config
                || this.selectedJobDetail.descParameterStore.reEvolveConfig;
            const filteredEvolveConfig = omit(evolveConfig, ['enable_cold_start', 'model', 'num_workers', 'name']);
            const jobDetail: JobStore = yield this.selectedJobDetail.fetchSaveUpdateJob({
                continue_evolve_config: {
                    evolve_mod: 1,
                    select_solutions: select_solutions.map(item => item.s_id),
                    select_solutions_info: JSON.stringify(select_solutions.map(item => pick(
                        item, ['s_id', 'generation', 'iteration_found', 'combined_score', 'path', 'island_id']
                    ))),
                    name: this.selectedJobDetail.descParameterStore.reEvolveConfig.name,
                    evolve_config: filteredEvolveConfig,
                    select_excellent_solutions: 1
                },
                reqdetails: params.reqdetails
            });
            this.updateSelectedJobDetail(jobDetail);
        } catch (error) {
            console.error('[syncSelectedSolutionsToBackend] 同步失败:', error);
            throw error;
        }
    }

    setDefaultSelectedSolutions(selectedSolutions: string[] = []) {
        this.selectedSolutions = this.allRecommendedSolutions.filter(solution =>
            selectedSolutions.includes(solution.s_id)
        );
    }

    /**
     * 批量更新系统推荐解的选择状态
     * 用于全选/取消全选等批量操作，避免多次触发防抖
     * @param systemSolutions 要设置的系统推荐解列表
     */
    updateSystemRecommendedSelection(systemSolutions: InitialSolutionFileInfo[]) {
        if (!this.selectedJobDetail) {
            return;
        }

        // 1. 过滤掉所有当前选中的系统推荐解
        const nonSystemSolutions = this.selectedSolutions.filter(solution =>
            !this.systemRecommendedSolution.some(sysSolution =>
                sysSolution.s_id === solution.s_id
            )
        );

        // 2. 合并非系统解 + 新的系统推荐解
        const newSolutions = [...nonSystemSolutions];

        for (const solution of systemSolutions) {
            // 防止重复添加
            const exists = newSolutions.some(s => s.s_id === solution.s_id);
            if (!exists) {
                newSolutions.push(solution);
            }
        }

        // 3. 一次性更新
        this.selectedSolutions = newSolutions;

        // 4. 触发一次防抖同步
        this.debouncedSync();
    }

    /**
     * 添加解到已选择列表
     * @param solution 解数据，可以是单个对象或数组
     */
    addSolution(solution: InitialSolutionFileInfo | InitialSolutionFileInfo[]) {
        if (!this.selectedJobDetail) {
            return;
        }

        const solutionsToAdd = Array.isArray(solution) ? solution : [solution];
        const newSolutions = [...this.selectedSolutions];

        for (const solutionToAdd of solutionsToAdd) {
            // 防止重复添加
            const exists = newSolutions.some(s => s.s_id === solutionToAdd.s_id);
            if (!exists) {
                newSolutions.push(solutionToAdd);
            }
        }

        // 使用数组替换确保 MobX 正确追踪变化
        if (newSolutions.length > this.selectedSolutions.length) {
            this.selectedSolutions = newSolutions;
            // 触发防抖同步
            this.debouncedSync();
        }
    }

    /**
     * 从已选择列表中移除解
     * @param solutionId 解ID
     */
    removeSolution(solutionId: InitialSolutionFileInfo) {
        if (!this.selectedJobDetail) {
            return;
        }

        // 过滤掉要移除的解
        const updatedSolutions = this.selectedSolutions.filter(
            s => s.s_id !== solutionId.s_id
        );
        this.selectedSolutions = updatedSolutions;
        // 触发防抖同步
        this.debouncedSync();
    }

    /**
     * 清空已选择的解列表
     */
    clearSolutions(): void {
        this.selectedSolutions = [];
    }

    /**
     * 检查某个解是否已被选中
     * @param solutionId 解ID
     * @returns 是否已选中
     */
    hasSolution(solutionId: string): boolean {
        return this.selectedSolutions.some(s => s.s_id === solutionId);
    }

    /** 更新所有解的数据，以及默认选中的解 */
    setRecommendedSolutions(list: InitialSolutionFileInfo[]) {
        this.allRecommendedSolutions = list;
        if (
            !this.selectedJobDetail
            || !this.selectedJobDetail.select_solutions
        ) {
            return;
        }
        const selectedIds: string[] = this.selectedJobDetail?.select_solutions || [];

        this.selectedSolutions = list.filter(solution =>
            selectedIds.includes(solution.s_id)
        );
    }

    /** 设置系统推荐解表格数据 */
    setSystemRecommendedSolution(list: InitialSolutionFileInfo[]) {
        this.systemRecommendedSolution = list;
    }

    /**
     * 重置TaskEvoRunStore状态
     * 用于任务切换时的清理
     */
    reset(): void {
        // 清理防抖定时器
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        this.job_id = '';
        this.allRecommendedSolutions = [];
        this.systemRecommendedSolution = [];
        this.selectedSolutions = [];
        this.selectedHighQualityAlgorithms = [];
        this.selectedJobDetail = null;
    }

    /**
     * 更新 selectedJobDetail 引用
     * @param selectedJobDetail 最新的任务详情
     */
    updateSelectedJobDetail(selectedJobDetail: JobStore | null): void {
        this.selectedJobDetail = selectedJobDetail;
        // 只在初始化时设置默认选择，后续更新时保留用户选择
        if (this.selectedSolutions.length === 0 && selectedJobDetail?.select_solutions?.length) {
            this.setDefaultSelectedSolutions(selectedJobDetail?.select_solutions || []);
        }
    }

    /**
     * 防抖同步到后端
     * 用户操作后 1.5 秒自动同步，避免频繁请求
     */
    private debouncedSync() {
        // 清除之前的定时器
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // 设置新的定时器
        this.debounceTimer = setTimeout(() => {
            flowResult(this.syncSelectedSolutionsToBackend({
                reqdetails: false // 防抖同步不重新获取详情
            })).catch((error: unknown) => {
                console.error('[debouncedSync] 自动同步失败:', error);
                // 静默失败，不打断用户操作
            });
        }, 1000); // 1.5 秒后同步
    }
}
